
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { DateTime } from 'luxon';
import { scrollToFirstError } from '@/utils/errorHandling';
import { PERMISSION } from '@/permissions';
import Toast from '@/utils/toast';
import ContactInformation from './form/ContactInformation.vue';
import CustomFields from './form/CustomFields.vue';
import DeliveryInformation from './form/DeliveryInformation.vue';
import DiscountInformation from './form/DiscountInformation.vue';
import EventInformation from './form/EventInformation.vue';
import ExpansionPanel from '@/components/shared/suites/panels/ExpansionPanel.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import MembershipInformation from './form/MembershipInformation.vue';
import ModifyFee from '@/components/orders/cart/ModifyFee.vue';
import PaymentInformation from './form/PaymentInformation.vue';
import QuoteInformation from './form/QuoteInformation.vue';
import SpecialRequests from './form/SpecialRequests.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TakeoutInformation from './form/TakeoutInformation.vue';

const namespace = 'orders';

@Component<GuestDetailsModal>({
	components: {
		ContactInformation,
		CustomFields,
		DeliveryInformation,
		DiscountInformation,
		EventInformation,
		ExpansionPanel,
		IconButton,
		MembershipInformation,
		ModifyFee,
		PaymentInformation,
		QuoteInformation,
		SpecialRequests,
		StandardModal,
		TakeoutInformation,
		ValidationObserver
	}
})
export default class GuestDetailsModal extends Vue {
	@Action('submitOrder', { namespace }) submitOrder!: (inquiryId: number | null) => Promise<void>;
	@Action('updateOrderModificationRequest', { namespace }) private updateOrderModificationRequest!: (payload: OrderModificationRequest) => Promise<void>;
	@Action('setCheckoutAutoAccept', { namespace }) private setCheckoutAutoAccept!: (autoAccept: boolean) => void;
	@Action('setCheckoutAnsweredQuestions', { namespace }) private setCheckoutAnsweredQuestions!: (answeredQuestions: AnsweredCustomQuestion[]) => void;
	@Getter('getOrderStatus', { namespace }) orderStatus!: string | undefined;
	@Getter('getOrderDetails', { namespace }) orderDetails!: OrderDetails;
	@Getter('getCheckoutInfo', { namespace }) checkout!: Checkout;
	@Getter('getDiscountInfo', { namespace }) orderDiscounts!: CostsDiscount;
	@Getter('getModifyingOrder', { namespace }) modifyingOrder!: boolean;
	@Getter('isQuoting', { namespace }) private isQuoting!: boolean;
	@Getter('getActiveModificationRequest', { namespace }) private activeModificationRequest!: OrderModificationRequest | null;
	@Getter('getDeliveryInformation', { namespace }) private deliveryInformation!: DeliveryInfo | null;
	@Getter('getCosts', { namespace }) private costs!: Costs;
	@Getter('getServiceChargeLabel', { namespace }) private serviceChargeLabel!: string;
	@Getter('getCheckoutAnsweredQuestions', { namespace: 'orders' }) private checkoutAnsweredQuestions!: AnsweredCustomQuestion[] | null;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'auth' }) private restaurantDeliveryInfo!: DeliveryInfo | null;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('getMembershipPrograms', { namespace: 'auth' }) private membershipPrograms!: string[] | null;
	@Getter('getMembershipCustomization', { namespace: 'auth' }) private membershipCustomization!: MembershipCustomization;
	@Getter('getDiscounts', { namespace: 'auth' }) private discounts!: DiscountInfo;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getSelectedInquiryFormRequest', { namespace: 'inquiryForm' }) private inquiryFormRequest!: InquiryFormRequest;
	@Prop({ type: Array, required: true }) private menus!: Menu[];
	@Prop({ type: Boolean, default: false }) showModal!: boolean;
	@Prop({ type: Boolean, default: false }) private isKiosk!: boolean;
	@Prop({ type: String, required: false }) private returnPath!: string;

	$refs!: { observer: Validate; };

	private PERMISSION = PERMISSION;
	private coordinatesErrorMsg: string = '';
	private fetchingSchedulingInfo: boolean = false;
	private isValidating: boolean = false;
	private invalidDeliveryConfig: boolean = false;
	private inquiryId: number | null = null;
	private modifyingFee: Record<FeeType, boolean> = { serviceCharge: false, deliveryCharge: false };

	/**
	 * Whether the current order is a catering event.
	 *
	 * @return {boolean}
	 */
	private get isCateringEvent(): boolean {
		return this.orderDetails?.takeoutType === 'catering-event';
	}

	private get showTakeoutDetails(): boolean {
		return this.orderDetails.type === 'take-out' || this.orderDetails.type === 'generic-catering';
	}

	private get confirmButtonText(): string {
		if (this.isKiosk) {
			return this.$t('orders.guest_details.pay_now_btn') as string;
		}
		return this.modifyingOrder || this.orderDetails.type === 'generic-catering'
			? this.$t('orders.guest_details.submit_btn')
			: this.$t('orders.guest_details.send_to_kitchen_btn');
	}

	/**
	 * Check if the discount section should be displayed
	 * - If catering and manager role
	 * - If promo codes are available
	 * - If a discount was applied
	 *
	 * @return {boolean}
	 */
	private get showDiscountSection(): boolean {
		if (this.orderDetails.type === 'generic-catering' && this.hasPermission(PERMISSION.ORDER_CATERING_DISCOUNT)) {
			return true;
		}
		return !!(this.discounts.codes?.length || this.orderDiscounts?.amount > 0);
	}

	/**
	 * Check if the delivery configuration is valid
	 * First check if the restaurant has delivery info
	 * Then check that the order type is delivery or generic catering
	 * * Generic catering can now as of 2025-05-08 support pickup, so we now also check if there is a delivery address
	 * Then check that the delivery type is address or custom (school and stadium are not supported)
	 *
	 * @return {boolean}
	 */
	private get showDeliveryDetails(): boolean {
		const hasDeliveryInfo = !!(this.restaurantDeliveryInfo && Object.keys(this.restaurantDeliveryInfo).length);
		const isOrderTypeValid = this.orderDetails?.takeoutType === 'delivery' || (this.orderDetails?.type === 'generic-catering' && !!this.deliveryInformation?.address);
		const isDeliveryTypeValid = ['address', 'open_address', 'custom'].includes(this.restaurantDeliveryInfo?.type || '');

		return hasDeliveryInfo && isDeliveryTypeValid && isOrderTypeValid;
	}

	private get showDeliveryFeeModifier(): boolean {
		// Custom (radius-based) fees depend on the delivery address, so one must be entered before the fee can be shown.
		// Otherwise a configured flat/percentage fee can always be shown, even when the calculated fee is $0.
		// A modified fee is always available so it can be seen and reset, even when no fee is configured
		const showFeeEditor = !!this.costs.deliveryCharge.feeModified
			|| (this.restaurant.delivery?.delivery_fee_type === 'custom' ? !!this.deliveryInformation?.address : !!this.restaurant.delivery?.delivery_charge);

		return this.orderDetails.type === 'generic-catering'
			&& this.hasPermission(PERMISSION.ORDER_MODIFY)
			&& this.showDeliveryDetails
			&& showFeeEditor;
	}

	private created(): void {
		// If the order type is delivery, but the restaurant does not have valid delivery settings, show an error message and disable the submit button
		// Catering event quotes are always either address or open-ended addres delivery and ignore delivery settings
		if (this.checkout.pickup?.delivery && !this.showDeliveryDetails && this.orderDetails.takeoutType !== 'catering-event') {
			Toast.error(this.$t('orders.guest_details.error_invalid_delivery_config'));
			this.invalidDeliveryConfig = true;
		}
		// Set answered questions from inquiry form as answered checkout questions
		if (!this.modifyingOrder && this.isQuoting && !this.checkoutAnsweredQuestions?.length && this.inquiryFormRequest?.additional_questions?.length) {
			this.setCheckoutAnsweredQuestions(this.inquiryFormRequest.additional_questions);
		}
		this.inquiryId = this.$route.query?.inquiryRequestId ? Number(this.$route.query.inquiryRequestId) : null;
	}

	/**
	 * Calculates great-circle distances between two points using the
	 * harversine formula (keep in mind this is in a "crow" distance,
	 * meaning it does not count the obstacles in the way of the point
	 * A and B). The haversine formula also does not account for the earth
	 * not being perfectly spheroid, meaning there is a margin of error,
	 * but since this is used for a approximate distance, the margin is
	 * acceptable. If we want to make this even better, we could even use spherical
	 * geometry from GMaps API.
	 * For more info on the formula, see: https://en.wikipedia.org/wiki/Haversine_formula
	 *
	 * @param {number} lat1
	 * @param {number} lng1
	 * @param {number} lat2
	 * @param {number} lng2
	 * @return {number}
	 */
	private getDistanceFromLatLonInKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
		const earthRadiusInKilometer = 6371;
		const dLat = this.degreeToRad(lat2 - lat1);
		const dLon = this.degreeToRad(lng2 - lng1);

		// Square of half the chord length between the points
		const squareChordLength =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(this.degreeToRad(lat1)) * Math.cos(this.degreeToRad(lat2)) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2);

		// Angular distance in radians.
		var angularDistance = 2 * Math.atan2(Math.sqrt(squareChordLength), Math.sqrt(1 - squareChordLength));
		var distanceInKilometer = earthRadiusInKilometer * angularDistance;
		return distanceInKilometer;
	}

	/**
	 * Takes a degree value and translates it to
	 * a radian. Opted out of using the Number prototype toRad as
	 * it was being inconsistant in some cases.
	 *
	 * @param {number} degree
	 * @return {number}
	 */
	private degreeToRad(degree: number): number {
		return degree * (Math.PI / 180);
	}

	/**
	 * Validate all the input fields, save the pickup information if it's
	 * a take out order and save the user contact information if it's
	 * an anonymous order and proceed to the review screen.
	 *
	 * @return {Promise<void>}
	 */
	private async validateAndSubmit(): Promise<any> {
		this.isValidating = true;
		let isValid = await this.$refs.observer.validate();
		let deliveryValid = true;

		// Validate delivery radius
		if(this.checkout.pickup?.delivery) {
			deliveryValid = this.validateDelivery();
		}

		// If everything is validated, we can go next.
		if (isValid && deliveryValid) {
			this.coordinatesErrorMsg = '';
			this.setDueDateTimeIfAsapOrder();

			try {
				await this.submitOrder(this.inquiryId);

				if (this.modifyingOrder) {
					Toast.success(this.$t('orders.guest_details.modify_success'));
					this.updateModificationRequest();
					this.$router.push(this.returnPath || '/order-management').catch(() => {});
				}
				else {
					this.$emit('order-submitted');
				}
			}
			catch (errorMessage) {
				Toast.error(this.$t(this.isQuoting ? 'orders.guest_details.error_submitting_quote' : 'orders.guest_details.error_submitting_order', { errorMessage }));
			}
		}
		else {
			scrollToFirstError();
		}
		this.isValidating = false;
	}

	/**
	 * Set the due date and time for an asap order
	 *
	 * @return {void}
	 */
	private setDueDateTimeIfAsapOrder(): void {
		const { pickup } = this.checkout;
		if (pickup && !pickup.scheduled) {
			pickup.dueByDate = DateTime.local().toISO()!;
			pickup.dueByTime = DateTime.local().toFormat(this.$t('orders.guest_details.takeout.timepicker_format'));
			if (pickup.dueByTime.length === 7) {
				pickup.dueByTime = '0' + pickup.dueByTime;
			}
		}
	}

	/**
	 * Validate delivery address
	 *
	 * @return {boolean}
	 */
	private validateDelivery(): boolean {
		const { delivery } = this.checkout!;
		this.coordinatesErrorMsg = '';
		if (delivery.type === 'address') {
			if (delivery.lat && delivery.lng) {
				if (delivery.streetNumber) {
					const defaultDelivery: DeliveryInfo | null = this.restaurantDeliveryInfo;
					// No configured delivery info means no radius to validate against
					// Catering events can skip this since it always uses address or open-ended address delivery regardless of delivery configuration
					if (!defaultDelivery) {
						return this.orderDetails.takeoutType === 'catering-event';
					}

					const distance = this.getDistanceFromLatLonInKm(delivery.lat, delivery.lng, defaultDelivery.lat!, defaultDelivery.lng!);
					if (distance > delivery.radius!) {
						this.coordinatesErrorMsg = this.$t('orders.guest_details.delivery.error_address_exceed_radius');
						return false;
					}
					return true;
				}
				this.coordinatesErrorMsg = this.$t('orders.guest_details.delivery.error_address_not_precise');
				return false;
			}
			this.coordinatesErrorMsg = this.$t('orders.guest_details.delivery.error_address_invalid');
			return false;
		}
		if (delivery.type === 'open_address' && delivery.address) {
			return true;
		}
		if (delivery.location && delivery.area) {
			return true;
		}
		return false;
	}

	/**
	 * If there is an active modification request, update its status to accepted
	 * This is set from the OrderModificationRequestModal when the operator accepts the request
	 *
	 * @return {Promise<void>}
	 */
	private async updateModificationRequest(): Promise<void> {
		if (this.activeModificationRequest) {
			try {
				await this.updateOrderModificationRequest({
					...this.activeModificationRequest,
					status: 'accepted'
				});
			}
			catch (errorMessage) {
				Toast.error(this.$t('orders.management.modification_request_modal.error_updating_request', { errorMessage }));
			}
		}
	}
}
